import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import type { ChatUiPlanProposal } from "./hooks/useChatUiActions";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface ChatPlanApprovalQuestionDrawerProps {
  proposal: ChatUiPlanProposal | null;
  isSubmitting?: boolean;
  onApprove: (feedback?: string) => void | Promise<void>;
  onRequestChanges: (feedback: string) => void | Promise<void>;
  onCancel: () => void;
}

export function ChatPlanApprovalQuestionDrawer({
  proposal,
  isSubmitting = false,
  onApprove,
  onRequestChanges,
  onCancel,
}: ChatPlanApprovalQuestionDrawerProps) {
  const [feedback, setFeedback] = useState("");
  const [decision, setDecision] = useState<"yes" | "no">("yes");

  useEffect(() => {
    setFeedback("");
    setDecision("yes");
  }, [proposal?.actionId]);

  if (!proposal) return null;

  const requiresInstruction = decision === "no" && !feedback.trim();
  const submitLabel = decision === "yes" ? "Submit" : "Send";
  const submitDecision = async () => {
    if (decision === "yes") {
      await onApprove(feedback);
      return;
    }
    await onRequestChanges(feedback);
  };

  return (
    <div
      className="fintheon-chat-approval-overlay fintheon-chat-approval-overlay--plan"
      role="dialog"
      aria-label="Plan approval question"
    >
      <div className="fintheon-chat-approval-question">
        <div className="fintheon-chat-approval-meta">
          <span>Plan approval</span>
          <span>
            <FintheonNumericText text="1/1" />
          </span>
        </div>
        <p className="fintheon-chat-approval-summary">
          {proposal.summary ??
            "Review the plan above before Harper runs tools or changes state."}
        </p>
        <p className="fintheon-chat-approval-copy">
          Question: Implement the Plan?
        </p>
        <div
          className="fintheon-chat-plan-choice-list"
          role="radiogroup"
          aria-label="Implement the plan"
        >
          <button
            type="button"
            role="radio"
            aria-checked={decision === "yes"}
            data-selected={decision === "yes" ? "true" : "false"}
            onClick={() => setDecision("yes")}
            disabled={isSubmitting}
            className="fintheon-chat-plan-choice-row"
          >
            <span>
              <FintheonNumericText text="1." />
            </span>
            <strong>Yes, move forward</strong>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={decision === "no"}
            data-selected={decision === "no" ? "true" : "false"}
            onClick={() => setDecision("no")}
            disabled={isSubmitting}
            className="fintheon-chat-plan-choice-row"
          >
            <span>
              <FintheonNumericText text="2." />
            </span>
            <strong>No, tell CAO what to do instead</strong>
          </button>
        </div>
        <textarea
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
          placeholder={
            decision === "yes"
              ? "Optional: add constraints before Harper starts."
              : "Tell CAO what to change before implementation."
          }
          className="fintheon-chat-approval-textarea"
        />
      </div>

      <div className="fintheon-chat-approval-actions">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="fintheon-chat-approval-back"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => void submitDecision()}
          disabled={isSubmitting || requiresInstruction}
          className="fintheon-chat-approval-submit"
        >
          <Check size={12} />
          {isSubmitting ? "Submitting" : submitLabel}
        </button>
      </div>
    </div>
  );
}
